import { Flex, Form, Typography, theme } from "antd";
import type { AutoApprovalThreshold } from "@/common/model";
import { SegmentedSelection } from "@/components/segmented-selection";
import { StatusPill } from "@/components/status-pill";
import {
	AUTOMATION_RISK_ROWS,
	riskRunsUnattended,
	THRESHOLD_OPTIONS,
} from "./environment-base";

/**
 * Visual explainer for an automation policy: which risk tiers are auto-approved under it, and which
 * stop for a person. Rows re-tint as the policy changes, so the boundary is something you watch move
 * rather than a table you have to cross-reference.
 *
 * Reusable — the onboarding wizard, the edit wizard, and the policy drawer all render this from the
 * same single source of truth (`riskRunsUnattended`), which mirrors the backend's
 * `ShouldAutoApprove`.
 */
export function AutomationPolicyLadder({
	threshold,
}: {
	threshold: AutoApprovalThreshold;
}) {
	return (
		<Flex vertical gap={8} role="list" style={{ minWidth: 0 }}>
			{AUTOMATION_RISK_ROWS.map((row) => (
				<LadderRow
					key={row.key}
					label={row.label}
					detail={row.detail}
					autoApproved={riskRunsUnattended(threshold, row.key)}
				/>
			))}
		</Flex>
	);
}

function LadderRow({
	label,
	detail,
	autoApproved,
}: {
	label: string;
	detail: string;
	autoApproved: boolean;
}) {
	const {
		token: {
			colorSuccessBg,
			colorSuccessBorder,
			colorWarningBg,
			colorWarningBorder,
			borderRadius,
			motionDurationSlow,
			motionEaseInOut,
		},
	} = theme.useToken();

	return (
		<Flex
			role="listitem"
			align="center"
			gap={12}
			style={{
				padding: "8px 12px",
				// Same tinted-panel idiom as the approvals "Requested access" block: a full 1px
				// semantic border, never an accent bar.
				background: autoApproved ? colorSuccessBg : colorWarningBg,
				border: `1px solid ${autoApproved ? colorSuccessBorder : colorWarningBorder}`,
				borderRadius,
				// The tint and border shifting together IS the animation: changing the policy slides
				// the boundary between auto-approved and gated up or down the ladder.
				transition: `background-color ${motionDurationSlow} ${motionEaseInOut}, border-color ${motionDurationSlow} ${motionEaseInOut}`,
			}}
		>
			<div style={{ flex: 1, minWidth: 0 }}>
				<Typography.Text strong>{label}</Typography.Text>
				<Typography.Paragraph
					type="secondary"
					style={{ fontSize: 12, marginBottom: 0 }}
				>
					{detail}
				</Typography.Paragraph>
			</div>
			{/* Keyed on the outcome so React remounts it and the fade-in replays on every flip. */}
			<span
				key={String(autoApproved)}
				style={{
					whiteSpace: "nowrap",
					animation: `snLadderFlip ${motionDurationSlow} ${motionEaseInOut}`,
				}}
			>
				<StatusPill status={autoApproved ? "success" : "warning"}>
					{autoApproved ? "Auto-approved" : "Approval required"}
				</StatusPill>
			</span>
		</Flex>
	);
}

/** The form field the policy is stored under — matches the API's `autoApproveUpTo`. */
const FIELD_NAME = "autoApproveUpTo";

/**
 * The policy selector paired with its ladder, side by side when there is room and stacked when
 * there is not. This pairing is the unit worth reusing: a selector on its own leaves the reader to
 * work out what the policy covers, which is exactly what the ladder is for.
 *
 * Must be rendered inside an antd `Form` — it watches the field to keep the ladder live.
 */
export function AutomationPolicyField({
	initialValue,
}: {
	initialValue: AutoApprovalThreshold;
}) {
	const watched = Form.useWatch<AutoApprovalThreshold>(FIELD_NAME);
	const threshold = watched ?? initialValue;

	return (
		<Flex gap={16} wrap align="flex-start">
			<Form.Item
				name={FIELD_NAME}
				label="Automation policy"
				initialValue={initialValue}
				rules={[{ required: true }]}
				style={{ flex: "1 1 280px", minWidth: 0, marginBottom: 0 }}
			>
				<SegmentedSelection options={THRESHOLD_OPTIONS} columns={1} />
			</Form.Item>
			<div style={{ flex: "1 1 300px", minWidth: 0 }}>
				<Typography.Text>What that means for each action</Typography.Text>
				<div style={{ marginTop: 6 }}>
					<AutomationPolicyLadder threshold={threshold} />
				</div>
			</div>
		</Flex>
	);
}
